/**
 * DocsValidator - Validation logic for DocsApp_v2
 * 
 * Validates nodes to ensure they conform to docs specifications
 */

import { isClassNameSupported } from './DocsConfig';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

export class DocsValidator {
  /**
   * Validate a node
   */
  validate(node: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Check if node exists
    if (!node) {
      errors.push('Node is null or undefined');
      return { valid: false, errors, warnings };
    }
    
    // Get className
    const className = node.className || node.getAttribute?.('className');
    
    // Check if className is supported
    if (className && !isClassNameSupported(className)) {
      errors.push(`Unsupported className: ${className}`);
    }
    
    // Check position type (docs only supports static/relative)
    const positionType = node.attrs?.position?.type || 'static';
    if (positionType !== 'static' && positionType !== 'relative') {
      errors.push(`Invalid position type for docs: ${positionType}. Only 'static' and 'relative' are allowed.`);
    }
    
    // Class-specific validation
    if (className) {
      this.validateSpecificClass(className, node, errors, warnings);
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
  
  /**
   * Validate specific class
   */
  private validateSpecificClass(className: string, node: any, errors: string[], warnings: string[]): void {
    switch (className) {
      case 'paragraph':
        this.validateParagraph(node, errors, warnings);
        break;
        
      case 'heading-1':
      case 'heading-2':
      case 'heading-3':
      case 'heading-4':
      case 'heading-5':
      case 'heading-6':
        this.validateHeading(node, errors, warnings);
        break;
        
      case 'bullet-list':
      case 'ordered-list':
        this.validateList(node, errors, warnings);
        break;
        
      case 'list-item':
        this.validateListItem(node, errors, warnings);
        break;
        
      case 'code-block':
        this.validateCodeBlock(node, errors, warnings);
        break;
        
      case 'blockquote':
        this.validateBlockquote(node, errors, warnings);
        break;
        
      case 'callout-info':
      case 'callout-warning':
      case 'callout-success':
      case 'callout-error':
        this.validateCallout(node, errors, warnings);
        break;
        
      case 'image':
        this.validateImage(node, errors, warnings);
        break;
        
      case 'video':
        this.validateVideo(node, errors, warnings);
        break;
        
      case 'table':
        this.validateTable(node, errors, warnings);
        break;
        
      case 'link':
        this.validateLink(node, errors, warnings);
        break;
        
      case 'horizontal-rule':
        // No specific validation needed
        break;
        
      default:
        warnings.push(`No specific validation rules for className: ${className}`);
    }
  }
  
  /**
   * Validate paragraph
   */
  private validateParagraph(node: any, errors: string[], warnings: string[]): void {
    // Paragraphs should have text content (can be empty)
    const text = node.text || node.textContent || '';
    
    if (text.length > 10000) {
      warnings.push('Paragraph is very long (>10,000 characters). Consider splitting.');
    }
  }
  
  /**
   * Validate heading
   */
  private validateHeading(node: any, errors: string[], warnings: string[]): void {
    const text = node.text || node.textContent || '';
    
    if (!text || text.trim().length === 0) {
      warnings.push('Heading has no text content');
    }
    
    if (text.length > 200) {
      warnings.push('Heading is very long (>200 characters). Consider making it shorter.');
    }
  }
  
  /**
   * Validate list
   */
  private validateList(node: any, errors: string[], warnings: string[]): void {
    // Lists should have list-item children
    const children = node.content || node.children || [];
    
    if (children.length === 0) {
      warnings.push('List has no items');
    }
  }
  
  /**
   * Validate list item
   */
  private validateListItem(node: any, errors: string[], warnings: string[]): void {
    const text = node.text || node.textContent || '';
    
    if (!text || text.trim().length === 0) {
      warnings.push('List item has no text content');
    }
  }
  
  /**
   * Validate code block
   */
  private validateCodeBlock(node: any, errors: string[], warnings: string[]): void {
    const code = node.text || node.textContent || '';
    
    if (!code || code.trim().length === 0) {
      warnings.push('Code block has no code');
    }
    
    if (code.length > 50000) {
      warnings.push('Code block is very large (>50,000 characters). Consider splitting or linking to external file.');
    }
  }
  
  /**
   * Validate blockquote
   */
  private validateBlockquote(node: any, errors: string[], warnings: string[]): void {
    const text = node.text || node.textContent || '';
    
    if (!text || text.trim().length === 0) {
      warnings.push('Blockquote has no text content');
    }
  }
  
  /**
   * Validate callout
   */
  private validateCallout(node: any, errors: string[], warnings: string[]): void {
    const text = node.text || node.textContent || '';
    
    if (!text || text.trim().length === 0) {
      warnings.push('Callout has no text content');
    }
  }
  
  /**
   * Validate image
   */
  private validateImage(node: any, errors: string[], warnings: string[]): void {
    const src = node.attrs?.src || node.getAttribute?.('src');
    
    if (!src) {
      errors.push('Image has no src attribute');
    } else {
      // Check if src is valid URL or data URI
      if (!src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
        warnings.push('Image src should be a valid URL or data URI');
      }
    }
    
    const alt = node.attrs?.alt || node.getAttribute?.('alt');
    if (!alt) {
      warnings.push('Image should have alt text for accessibility');
    }
  }
  
  /**
   * Validate video
   */
  private validateVideo(node: any, errors: string[], warnings: string[]): void {
    const src = node.attrs?.src || node.getAttribute?.('src');
    
    if (!src) {
      errors.push('Video has no src attribute');
    }
  }
  
  /**
   * Validate table
   */
  private validateTable(node: any, errors: string[], warnings: string[]): void {
    // Tables should have rows
    const children = node.content || node.children || [];
    
    if (children.length === 0) {
      warnings.push('Table has no rows');
    }
  }
  
  /**
   * Validate link
   */
  private validateLink(node: any, errors: string[], warnings: string[]): void {
    const href = node.attrs?.href || node.getAttribute?.('href');
    
    if (!href) {
      errors.push('Link has no href attribute');
    } else {
      // Check if href is valid
      if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('/') && !href.startsWith('#')) {
        warnings.push('Link href should be a valid URL or path');
      }
    }
    
    const text = node.text || node.textContent || '';
    if (!text || text.trim().length === 0) {
      warnings.push('Link has no text content');
    }
  }
  
  /**
   * Validate multiple nodes
   */
  validateAll(nodes: any[]): { valid: boolean; results: ValidationResult[] } {
    const results = nodes.map(node => this.validate(node));
    const valid = results.every(r => r.valid);
    
    return { valid, results };
  }
  
  /**
   * Get validation summary
   */
  getValidationSummary(results: ValidationResult[]): {
    totalNodes: number;
    validNodes: number;
    invalidNodes: number;
    totalErrors: number;
    totalWarnings: number;
  } {
    return {
      totalNodes: results.length,
      validNodes: results.filter(r => r.valid).length,
      invalidNodes: results.filter(r => !r.valid).length,
      totalErrors: results.reduce((sum, r) => sum + r.errors.length, 0),
      totalWarnings: results.reduce((sum, r) => sum + (r.warnings?.length || 0), 0)
    };
  }
}

